import React, { useState, useEffect, useCallback } from 'react';
import { PropTypes } from 'prop-types';
import axiosInstance from 'utils/axiosUtil';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DateUtil } from 'doctivity-shared/utils';
import {
  Badge,
  IconButton,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import { SmartMarkdownLink } from './SmartMarkdownLink';

/**
 * Notification bell + inbox for the current user. Reads /api/v2/my/notifications
 * (that user's active recipient rows — a flat per-user lookup), and marks a
 * message read when opened. Rendered beside the dashboard greeting.
 */
const NotificationBell = ({ sx }) => {
  const [items, setItems] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selected, setSelected] = useState(null);

  const unread = items.filter((i) => !i.read_at).length;

  const load = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/v2/my/notifications');
      setItems(Array.isArray(data?.notifications) ? data.notifications : []);
    } catch (e) {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openInbox = (e) => setAnchorEl(e.currentTarget);
  const closeInbox = () => setAnchorEl(null);

  const openItem = async (item) => {
    setSelected(item);
    if (!item.read_at) {
      // Optimistically mark read locally, then persist.
      setItems((list) => list.map((i) => (i.id === item.id ? { ...i, read_at: new Date().toISOString() } : i)));
      try {
        await axiosInstance.post(`/v2/my/notifications/${item.id}/read`);
      } catch (e) {
        /* non-fatal */
      }
    }
  };

  // Delete a notification from the user's own list (soft-delete of their recipient
  // row, server-side). Works for any message type, announcements included.
  const deleteMessage = async (item) => {
    setItems((list) => list.filter((i) => i.id !== item.id));
    setSelected((cur) => (cur && cur.id === item.id ? null : cur));
    try {
      await axiosInstance.delete(`/v2/my/notifications/${item.id}`);
    } catch (e) {
      /* non-fatal */
    }
  };

  return (
    <Box sx={sx}>
      <IconButton
        onClick={openInbox}
        size='large'
        color='inherit'
        aria-label='Notifications'
        // Idle (nothing to read) → muted so it doesn't pop; unread or hover → full color.
        sx={{
          opacity: unread > 0 ? 1 : 0.4,
          transition: 'opacity 0.15s ease',
          '&:hover': { opacity: 1 },
        }}
      >
        <Badge badgeContent={unread} color='error'>
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeInbox}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ width: 360, maxWidth: '90vw' }}>
          <Typography variant='subtitle1' sx={{ px: 2, py: 1.5, fontWeight: 600 }}>
            Notifications
          </Typography>
          <Divider />
          {items.length === 0 ? (
            <Typography sx={{ p: 2 }} color='textSecondary'>
              You&apos;re all caught up.
            </Typography>
          ) : (
            <List disablePadding sx={{ maxHeight: 420, overflowY: 'auto' }}>
              {items.map((item) => (
                <ListItemButton
                  key={item.id}
                  onClick={() => {
                    closeInbox();
                    openItem(item);
                  }}
                  sx={{ '& .delete-btn': { visibility: 'hidden' }, '&:hover .delete-btn': { visibility: 'visible' } }}
                >
                  <ListItemText
                    primary={item.title}
                    secondary={item.created_at ? DateUtil.formatFromDB(item.created_at) : ''}
                    primaryTypographyProps={{ fontWeight: item.read_at ? 400 : 700 }}
                  />
                  {!item.read_at && (
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main', ml: 1 }} />
                  )}
                  <IconButton
                    className='delete-btn'
                    size='small'
                    aria-label='Delete'
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteMessage(item);
                    }}
                    sx={{ ml: 0.5 }}
                  >
                    <CloseIcon fontSize='small' />
                  </IconButton>
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      </Popover>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth='sm'>
        {selected && (
          <>
            <DialogContent dividers>
              <Typography variant='h6' gutterBottom>{selected.title}</Typography>
              {/* Wrap so the Markdown body uses the app's (theme) font, not the browser default. */}
              <Box sx={{ typography: 'body1' }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: SmartMarkdownLink }}>{selected.body_markdown || ''}</ReactMarkdown>
              </Box>
            </DialogContent>
            <DialogActions>
              {/* Any message can be deleted; closing (or Got it!) keeps it as read. */}
              <Button onClick={() => deleteMessage(selected)} color='primary'>Delete</Button>
              {selected.link_url ? (
                <Button href={selected.link_url} variant='contained' color='primary'>
                  {selected.link_url.includes('/mileage') ? 'View Mileage' : 'View Note'}
                </Button>
              ) : (
                <Button onClick={() => setSelected(null)} variant='contained' color='primary'>Got it!</Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

NotificationBell.propTypes = {
  sx: PropTypes.object,
};

export { NotificationBell };
export default NotificationBell;
